import { z } from 'zod';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { ApiError } from './api';

/**
 * Cliente HTTP + hooks para la API de preferencias semanales (M2.31.A).
 *
 * No usamos el helper `request` de `./api` porque este endpoint no involucra
 * a Anthropic ni necesita el mapeo de errores 503/502/422 de la familia
 * "coach". Es un CRUD plano del perfil del usuario.
 *
 * Convenciones:
 * - `['profile', 'weekly-preferences']` como query key.
 * - El PUT invalida la query para que cualquier vista que muestre estas
 *   preferencias (formulario o futura tarjeta resumen) las recargue.
 */

const API_BASE_URL: string = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

// ---------------------------------------------------------------------------
// Schemas y tipos
// ---------------------------------------------------------------------------

/**
 * Disciplinas permitidas para un día. `'rest'` es explícito (el usuario
 * quiere descanso ese día); `null` significa "sin preferencia" (el coach
 * decide).
 *
 * Mantenemos la lista alineada con el contrato backend M2.31.A:
 *   swim | bike | run | strength | brick | rest
 */
export const DISCIPLINE_PREFERENCES = [
  'swim',
  'bike',
  'run',
  'strength',
  'brick',
  'rest',
] as const;
export type DisciplinePreference = (typeof DISCIPLINE_PREFERENCES)[number];

export const DisciplinePreferenceSchema = z.enum(DISCIPLINE_PREFERENCES);

/**
 * Acepta también `null` (sin preferencia). Algunos backends devuelven
 * `undefined` por error; lo normalizamos a `null` en el cliente para que la
 * UI no tenga que distinguir.
 */
const DayDisciplineSchema = DisciplinePreferenceSchema.nullable();

/**
 * Hora local en formato `HH:MM` (24h). El backend persiste un string libre,
 * pero la UI sólo permite horas en punto (00:00, 01:00, …, 23:00).
 *
 * Aceptamos también `H:MM` (sin cero a la izquierda) para no romper con
 * datos legacy.
 */
const HourLocalSchema = z
  .string()
  .regex(/^([01]?\d|2[0-3]):[0-5]\d$/, 'Hora local debe tener formato HH:MM');

export const WeeklyPreferencesSchema = z.object({
  monDiscipline: DayDisciplineSchema,
  tueDiscipline: DayDisciplineSchema,
  wedDiscipline: DayDisciplineSchema,
  thuDiscipline: DayDisciplineSchema,
  friDiscipline: DayDisciplineSchema,
  satDiscipline: DayDisciplineSchema,
  sunDiscipline: DayDisciplineSchema,
  autoGenerateWeekly: z.boolean(),
  autoGenerateNutrition: z.boolean(),
  autoGenerateHourLocal: HourLocalSchema,
});
export type WeeklyPreferences = z.infer<typeof WeeklyPreferencesSchema>;

/**
 * Para el PUT, todos los campos son opcionales (subset patch). Lo
 * resolvemos como `Partial<WeeklyPreferences>` para no duplicar la
 * definición de cada campo.
 */
export const WeeklyPreferencesUpdateSchema = WeeklyPreferencesSchema.partial();
export type WeeklyPreferencesUpdate = z.infer<typeof WeeklyPreferencesUpdateSchema>;

// ---------------------------------------------------------------------------
// HTTP
// ---------------------------------------------------------------------------

async function readJsonSafe(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

/** GET /api/profile/weekly-preferences */
export async function getWeeklyPreferences(): Promise<WeeklyPreferences> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/profile/weekly-preferences`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (response.status !== 200) {
    throw new ApiError(
      `Respuesta no OK del backend: ${response.status}`,
      response.status,
    );
  }
  const json = await readJsonSafe(response);
  const parsed = WeeklyPreferencesSchema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError(
      'Respuesta /api/profile/weekly-preferences no cumple esquema',
      200,
      parsed.error,
    );
  }
  return parsed.data;
}

/** PUT /api/profile/weekly-preferences (acepta subset). */
export async function updateWeeklyPreferences(
  patch: WeeklyPreferencesUpdate,
): Promise<WeeklyPreferences> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/profile/weekly-preferences`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(patch),
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (response.status !== 200) {
    throw new ApiError(
      `Respuesta no OK del backend: ${response.status}`,
      response.status,
    );
  }
  const json = await readJsonSafe(response);
  const parsed = WeeklyPreferencesSchema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError(
      'Respuesta PUT /api/profile/weekly-preferences no cumple esquema',
      200,
      parsed.error,
    );
  }
  return parsed.data;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export const WEEKLY_PREFERENCES_QUERY_KEY = [
  'profile',
  'weekly-preferences',
] as const;

export function useWeeklyPreferences(): UseQueryResult<WeeklyPreferences, Error> {
  return useQuery<WeeklyPreferences, Error>({
    queryKey: WEEKLY_PREFERENCES_QUERY_KEY,
    queryFn: getWeeklyPreferences,
    retry: false,
    staleTime: 30_000,
  });
}

export function useUpdateWeeklyPreferences(): UseMutationResult<
  WeeklyPreferences,
  Error,
  WeeklyPreferencesUpdate
> {
  const qc = useQueryClient();
  return useMutation<WeeklyPreferences, Error, WeeklyPreferencesUpdate>({
    mutationFn: updateWeeklyPreferences,
    onSuccess: (data) => {
      // Optimización: el PUT devuelve el row completo, así que actualizamos
      // el cache directamente además de invalidar (esto último cubre por si
      // hubiera otros consumidores con queryKey distinta).
      qc.setQueryData(WEEKLY_PREFERENCES_QUERY_KEY, data);
      void qc.invalidateQueries({ queryKey: WEEKLY_PREFERENCES_QUERY_KEY });
    },
  });
}

// ---------------------------------------------------------------------------
// Helpers de display (puros)
// ---------------------------------------------------------------------------

export function disciplinePreferenceLabel(d: DisciplinePreference): string {
  switch (d) {
    case 'swim':
      return 'Natación';
    case 'bike':
      return 'Bici';
    case 'run':
      return 'Carrera';
    case 'strength':
      return 'Fuerza';
    case 'brick':
      return 'Brick';
    case 'rest':
      return 'Descanso';
  }
}

/** Devuelve la lista canónica `['00:00', '01:00', …, '23:00']`. */
export function hoursOfDay(): string[] {
  const out: string[] = [];
  for (let h = 0; h < 24; h++) {
    out.push(`${String(h).padStart(2, '0')}:00`);
  }
  return out;
}
